/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import SecurityIcon from '@material-ui/icons/Security';
import { Progress } from '@backstage/core-components';
import {
  CalledByEntry,
  Change,
  compareChartRevisions,
  NetworkPolicyGraph,
  PortRule,
  promoteHops,
  ServiceDetail,
} from './types';
import { EnablePolicyDialog } from './EnablePolicyDialog';
import { changeKey } from './StagedChangesPanel';
import { useNetworkPolicyApi } from './useNetworkPolicyApi';

const useStyles = makeStyles(theme => ({
  paper: { width: 420, padding: theme.spacing(2) },
  header: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  section: { marginTop: theme.spacing(2) },
  callerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  envChip: { height: 18, fontSize: 10, marginLeft: 4 },
  addForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

// "8080, 9090/udp" -> PortRule[]
export const parsePortsInput = (input: string): PortRule[] | undefined => {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  return trimmed.split(',').map(part => {
    const [portStr, proto] = part.trim().split('/');
    const port = Number(portStr);
    if (!Number.isInteger(port) || port <= 0 || port > 65535) {
      throw new Error(`invalid port "${part.trim()}"`);
    }
    return proto?.toLowerCase() === 'udp'
      ? { port, protocol: 'UDP' as const }
      : { port };
  });
};

type Props = {
  service: string | null;
  graph: NetworkPolicyGraph;
  canEdit: boolean;
  advanced: boolean;
  stagedChanges: Change[];
  onStage: (change: Change) => void;
  onClose: () => void;
};

export const ServiceDetailDrawer = ({
  service,
  graph,
  canEdit,
  advanced,
  stagedChanges,
  onStage,
  onClose,
}: Props) => {
  const classes = useStyles();
  const api = useNetworkPolicyApi();
  const [detail, setDetail] = useState<ServiceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // Full {name, namespace} captured at pick time, not a name re-resolved
  // against the live graph at stage time: a background graph refresh that
  // momentarily drops the picked node would otherwise turn the Stage click
  // into a silent no-op (same class as the EnablePolicyDialog fix).
  const [addCaller, setAddCaller] = useState<{
    name: string;
    namespace: string;
  } | null>(null);
  const [addPorts, setAddPorts] = useState('');
  const [addEnvs, setAddEnvs] = useState<string[]>([]);
  const [addError, setAddError] = useState<string | null>(null);
  const [enableOpen, setEnableOpen] = useState(false);
  const [promoteConfirm, setPromoteConfirm] = useState<{
    fromEnv: string;
    toEnv: string;
  } | null>(null);
  const [promoteAttested, setPromoteAttested] = useState(false);

  // Stable identity of the staged set, not just its length: a same-length
  // mutation (e.g. a remove + an add landing in the same tick) would leave
  // `stagedChanges.length` unchanged and skip the refetch below, showing
  // stale caller data. Joining each change's own key (a primitive string)
  // gives value-based dependency comparison instead of length or reference.
  const stagedKey = stagedChanges.map(changeKey).join('|');

  // The add-caller form resets ONLY when the drawer switches to a different
  // service. It must NOT be keyed on stagedKey: stagedChanges is the
  // app-wide staged set, so staging any unrelated change (e.g. a removal of
  // a different caller, even on another service) would otherwise wipe a
  // half-completed form (picked caller, typed ports, checked envs) with no
  // warning.
  useEffect(() => {
    setAdding(false);
    setAddCaller(null);
    setAddPorts('');
    setAddEnvs([]);
    setAddError(null);
  }, [service]);

  // Detail refetches on service change AND on staged-set changes (an Apply
  // clears the staged set, and the refetched detail then shows the new
  // merged state). Kept separate from the form-reset effect above.
  useEffect(() => {
    setDetail(null);
    setError(null);
    if (!service) return undefined;
    let active = true;
    api
      .getService(service)
      .then(d => active && setDetail(d))
      .catch(e => active && setError(e.message));
    return () => {
      active = false;
    };
  }, [service, api, stagedKey]);

  // Union of callers across envs, with the envs each appears in and each
  // env's own port restriction. Ports can legitimately diverge per env
  // (grant-PR syncs retain pre-existing per-env restrictions), so the entry
  // records every env's ports rather than silently showing the first env's.
  const callers = useMemo(() => {
    const map = new Map<
      string,
      { entry: CalledByEntry; envs: string[]; portsByEnv: Map<string, CalledByEntry['ports']> }
    >();
    for (const f of detail?.files ?? []) {
      for (const entry of f.calledBy) {
        const key = `${entry.namespace}|${entry.name}`;
        const hit = map.get(key);
        if (hit) {
          hit.envs.push(f.env);
          hit.portsByEnv.set(f.env, entry.ports);
        } else {
          map.set(key, {
            entry,
            envs: [f.env],
            portsByEnv: new Map([[f.env, entry.ports]]),
          });
        }
      }
    }
    return [...map.values()];
  }, [detail]);

  const anyPolicy = (detail?.files ?? []).some(f => f.hasPolicy);
  const anyTraefik = (detail?.files ?? []).some(f => f.autoLockdownActive);
  const unprotectedEnvs = (detail?.files ?? [])
    .filter(f => !f.hasPolicy)
    .map(f => f.env);
  // Envs with no calledBy of their own, whose effective callers are entirely
  // whatever the base gitops/argocd/values.yaml file lists (Helm falls back
  // to base only when the env's own list is empty; a non-empty env list
  // replaces base wholesale).
  const baseFallbackEnvs = (detail?.files ?? [])
    .filter(f => f.usesBaseFallback)
    .map(f => f.env);
  const candidates = graph.nodes
    .filter(n => n.name !== service && n.namespace)
    .map(n => ({ name: n.name, namespace: n.namespace! }));

  // Convention: NetworkPolicy config is identical across all envs by
  // default, and enforcement is now on in every platform environment, so
  // treat every env with a policy as live. Default new callers to every
  // eligible env. Envs without a networkPolicy block are not eligible: the
  // backend rejects addCaller there (enabling default-deny must go through
  // the enablePolicy flow instead).
  const eligibleAddEnvs = (detail?.files ?? [])
    .filter(f => f.hasPolicy)
    .map(f => f.env);

  const stageAdd = () => {
    if (!service || !addCaller || addEnvs.length === 0) return;
    let ports: PortRule[] | undefined;
    try {
      ports = parsePortsInput(addPorts);
    } catch (e) {
      setAddError((e as Error).message);
      return;
    }
    onStage({
      op: 'addCaller',
      service,
      caller: { name: addCaller.name, namespace: addCaller.namespace, ports },
      envs: addEnvs,
    });
    setAdding(false);
    setAddCaller(null);
    setAddPorts('');
    setAddEnvs([]);
    setAddError(null);
  };

  return (
    <Drawer
      anchor="right"
      open={service !== null}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <Typography variant="h6" style={{ flexGrow: 1 }}>
          {service}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </div>
      {error && <Alert severity="error">{error}</Alert>}
      {!detail && !error && <Progress />}
      {detail && (
        <>
          <Typography variant="body2" color="textSecondary">
            {detail.node.namespace} · team {detail.node.team}
          </Typography>

          {!anyPolicy && anyTraefik && (
            <Alert severity="info" className={classes.section}>
              No peer allow-list (calledBy), but chart autoLockdown admits
              Traefik + baseline. In-cluster peers still need calledBy entries.
            </Alert>
          )}
          {!anyPolicy && !anyTraefik && (
            <Alert severity="info" className={classes.section}>
              No peer allow-list and no Traefik autoLockdown: this service
              accepts ingress from anywhere in the cluster (where NetworkPolicy
              is enforced).
            </Alert>
          )}
          {anyPolicy && unprotectedEnvs.length > 0 && (
            <Alert severity="warning" className={classes.section}>
              No peer allow-list in: {unprotectedEnvs.join(', ')}
            </Alert>
          )}
          {anyTraefik && (
            <Typography
              variant="caption"
              color="textSecondary"
              className={classes.section}
              component="div"
            >
              Traefik lockdown active in:{' '}
              {(detail?.files ?? [])
                .filter(f => f.autoLockdownActive)
                .map(f => f.env)
                .join(', ')}
            </Typography>
          )}
          {baseFallbackEnvs.length > 0 && (
            <Alert severity="info" className={classes.section}>
              {baseFallbackEnvs.join(', ')} {baseFallbackEnvs.length === 1 ? 'has' : 'have'}{' '}
              no calledBy of its own; the callers shown below for{' '}
              {baseFallbackEnvs.length === 1 ? 'it' : 'them'} come from the base
              file ({detail.base?.path ?? 'gitops/argocd/values.yaml'}). Staging
              a caller there gives that env its own list, which then fully
              replaces base's (so re-add any base callers you still need).
            </Alert>
          )}

          <div className={classes.section}>
            <Typography variant="subtitle2">
              Allowed callers ({callers.length})
            </Typography>
            <Divider />
            {callers.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                {anyPolicy
                  ? 'Peer allow-list empty (baseline / Traefik only if exposed).'
                  : anyTraefik
                    ? 'No peer callers listed; Traefik + baseline still admitted.'
                    : 'Everything can call this service.'}
              </Typography>
            )}
            {callers.map(({ entry, envs, portsByEnv }) => {
              const fmtPorts = (ports: CalledByEntry['ports']) =>
                ports && ports.length > 0
                  ? ports.map(p => p.port).join(',')
                  : 'all ports';
              const distinct = new Set([...portsByEnv.values()].map(fmtPorts));
              const portsDiverge = distinct.size > 1;
              return (
              <div key={`${entry.namespace}|${entry.name}`} className={classes.callerRow}>
                <Typography variant="body2" style={{ flexGrow: 1 }}>
                  {entry.name}
                  <Typography component="span" variant="caption" color="textSecondary">
                    {' '}
                    ({entry.namespace})
                  </Typography>
                </Typography>
                {portsDiverge ? (
                  <Tooltip
                    title={`Port restriction differs per env: ${[...portsByEnv.entries()]
                      .map(([env, ports]) => `${env}: ${fmtPorts(ports)}`)
                      .join('; ')}`}
                  >
                    <Chip
                      className={classes.envChip}
                      label="ports vary by env"
                      color="secondary"
                      variant="outlined"
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    className={classes.envChip}
                    label={fmtPorts(entry.ports)}
                    variant="outlined"
                  />
                )}
                {envs.length < detail.files.length && (
                  <Tooltip
                    title={`Only in: ${envs
                      .map(e => (baseFallbackEnvs.includes(e) ? `${e} (via base)` : e))
                      .join(', ')}`}
                  >
                    <Chip className={classes.envChip} label={`${envs.length}/${detail.files.length} envs`} />
                  </Tooltip>
                )}
                {envs.length === detail.files.length &&
                  envs.some(e => baseFallbackEnvs.includes(e)) && (
                    <Tooltip
                      title={`Via base file in: ${envs
                        .filter(e => baseFallbackEnvs.includes(e))
                        .join(', ')}`}
                    >
                      <Chip className={classes.envChip} label="base" variant="outlined" />
                    </Tooltip>
                  )}
                {canEdit && (
                  <Tooltip title="Stage removal of this caller">
                    <IconButton
                      size="small"
                      aria-label={`remove ${entry.name}`}
                      onClick={() =>
                        service &&
                        onStage({
                          op: 'removeCaller',
                          service,
                          callerName: entry.name,
                          callerNamespace: entry.namespace,
                          // default to where the rule actually is, not 'all':
                          // don't silently converge env-divergent rules.
                          envs,
                        })
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              );
            })}
          </div>

          <div className={classes.section}>
            <Typography variant="subtitle2">
              Calls ({detail.callees.length})
            </Typography>
            <Divider />
            {detail.callees.map(e => (
              <Typography key={e.id} variant="body2">
                → {e.callee}
              </Typography>
            ))}
            {detail.callees.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No declared ingress into other services.
              </Typography>
            )}
          </div>

          {advanced && (
            <div className={classes.section}>
              <Typography variant="subtitle2">Advanced</Typography>
              <Divider />
              {detail.files.map(f => {
                const exposureBits = [
                  f.exposure.external && 'external',
                  f.exposure.internal && 'internal',
                ].filter(Boolean);
                return (
                  <Box key={f.env} style={{ marginTop: 8 }}>
                    <Typography variant="body2">
                      <b>{f.env}</b>
                    </Typography>
                    <Typography variant="caption" color="textSecondary" component="div">
                      chart: {f.chartRev ?? '(missing pin)'}
                      {' · '}
                      exposure:{' '}
                      {exposureBits.length > 0 ? exposureBits.join('+') : 'none'}
                      {f.autoLockdownOptOut && ' · autoLockdown: false'}
                      {f.autoLockdownActive && ' · Traefik lockdown'}
                    </Typography>
                  </Box>
                );
              })}
              {canEdit &&
                service &&
                promoteHops(detail.files.map(f => f.env)).map(hop => {
                  const fromFile = detail.files.find(f => f.env === hop.from);
                  const toFile = detail.files.find(f => f.env === hop.to);
                  if (!fromFile || !toFile) return null;
                  // Offer only hops the backend's promoteEnv would accept:
                  // it rejects missing/unparseable/cross-family pins (null)
                  // and downgrades (negative).
                  const pinCmp = compareChartRevisions(
                    fromFile.chartRev,
                    toFile.chartRev,
                  );
                  if (pinCmp === null || pinCmp < 0) return null;
                  const pinBehind = pinCmp > 0;
                  // Behind = missing caller OR same caller with a different
                  // port restriction (the backend sync updates ports too).
                  const callersBehind = fromFile.calledBy.some(s => {
                    const t = toFile.calledBy.find(
                      x => x.name === s.name && x.namespace === s.namespace,
                    );
                    return (
                      !t ||
                      JSON.stringify(t.ports ?? null) !==
                        JSON.stringify(s.ports ?? null)
                    );
                  });
                  if (!pinBehind && !callersBehind) return null;
                  const alreadyStaged = stagedChanges.some(
                    c =>
                      c.op === 'promoteEnv' &&
                      c.service === service &&
                      c.fromEnv === hop.from &&
                      c.toEnv === hop.to,
                  );
                  const isProd = hop.to === 'platform-prod';
                  return (
                    <Box
                      key={hop.to}
                      style={{
                        display: 'inline-block',
                        marginTop: 8,
                        marginRight: 8,
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        color={isProd ? 'secondary' : 'primary'}
                        disabled={alreadyStaged}
                        onClick={() => {
                          if (isProd) {
                            setPromoteAttested(false);
                            setPromoteConfirm({
                              fromEnv: hop.from,
                              toEnv: hop.to,
                            });
                          } else if (service) {
                            onStage({
                              op: 'promoteEnv',
                              service,
                              fromEnv: hop.from,
                              toEnv: hop.to,
                            });
                          }
                        }}
                      >
                        {hop.label}
                        {pinBehind
                          ? ` (${toFile.chartRev} → ${fromFile.chartRev})`
                          : ''}
                        {alreadyStaged ? ' — staged' : ''}
                      </Button>
                      {hop.skipped.length > 0 && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          component="div"
                        >
                          Skips{' '}
                          {hop.skipped
                            .map(e => e.replace('platform-', ''))
                            .join(', ')}
                          : not in this repo yet.
                        </Typography>
                      )}
                    </Box>
                  );
                })}
            </div>
          )}

          {canEdit && (
            <div className={classes.section}>
              {anyPolicy ? (
                adding ? (
                  <Box className={classes.addForm}>
                    <Autocomplete
                      options={candidates}
                      getOptionLabel={o => o.name}
                      getOptionSelected={(o, v) =>
                        o.name === v.name && o.namespace === v.namespace
                      }
                      value={addCaller}
                      onChange={(_, v) => setAddCaller(v)}
                      renderInput={params => (
                        <TextField {...params} label="Caller service" size="small" />
                      )}
                    />
                    <TextField
                      label="Ports (optional, e.g. 8080, 9090/udp)"
                      size="small"
                      value={addPorts}
                      error={addError !== null}
                      helperText={addError}
                      onChange={e => setAddPorts(e.target.value)}
                    />
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Environments
                      </Typography>
                      <FormGroup row>
                        {detail.files.map(f => (
                          <FormControlLabel
                            key={f.env}
                            control={
                              <Checkbox
                                size="small"
                                checked={addEnvs.includes(f.env)}
                                disabled={!f.hasPolicy}
                                onChange={e =>
                                  setAddEnvs(prev =>
                                    e.target.checked
                                      ? [...prev, f.env]
                                      : prev.filter(x => x !== f.env),
                                  )
                                }
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {f.env}
                                {!f.hasPolicy && ' (no peers)'}
                              </Typography>
                            }
                          />
                        ))}
                      </FormGroup>
                      <Typography variant="caption" color="textSecondary">
                        NetworkPolicy config is identical across all envs by
                        default, and enforcement is now on in every platform
                        environment, so treat every env with a policy as live:
                        any caller you omit is cut off. Envs without a
                        networkPolicy block are disabled: adding a caller there
                        must go through the enable-policy flow.
                      </Typography>
                    </Box>
                    {addEnvs.length < eligibleAddEnvs.length && (
                      <Alert severity="warning">
                        Unchecking an env means it will not get this caller,
                        diverging from the all-envs-identical convention. Diff
                        and re-sync the excluded env(s) so they don't break
                        when reached under enforcement:{' '}
                        {eligibleAddEnvs
                          .filter(e => !addEnvs.includes(e))
                          .join(', ')}
                        .
                      </Alert>
                    )}
                    <Box display="flex" style={{ gap: 8 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={!addCaller || addEnvs.length === 0}
                        onClick={stageAdd}
                      >
                        Stage
                      </Button>
                      <Button size="small" onClick={() => setAdding(false)}>
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setAddEnvs(eligibleAddEnvs);
                      setAdding(true);
                    }}
                  >
                    Allow new caller
                  </Button>
                )
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SecurityIcon />}
                  onClick={() => setEnableOpen(true)}
                >
                  Enable network policy…
                </Button>
              )}
            </div>
          )}
        </>
      )}
      {service && (
        <EnablePolicyDialog
          open={enableOpen}
          service={service}
          graph={graph}
          traefikAlreadyActive={anyTraefik}
          onClose={() => setEnableOpen(false)}
          onStage={change => {
            onStage(change);
            setEnableOpen(false);
          }}
        />
      )}
      <Dialog
        open={promoteConfirm !== null}
        onClose={() => setPromoteConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Promote network policy to prod?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This bumps the <b>prod</b> chart pin and syncs calledBy from{' '}
            {promoteConfirm?.fromEnv}. Atlantis will plan the prod terraform
            env on merge. It does <b>not</b> flip NetworkPolicy enforcement in
            prod.
          </Alert>
          <FormControlLabel
            style={{ marginTop: 12 }}
            control={
              <Checkbox
                checked={promoteAttested}
                onChange={e => setPromoteAttested(e.target.checked)}
              />
            }
            label="I understand this bumps the prod chart pin and Atlantis will plan"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!promoteConfirm || !promoteAttested}
            onClick={() => {
              if (!service || !promoteConfirm) return;
              onStage({
                op: 'promoteEnv',
                service,
                fromEnv: promoteConfirm.fromEnv,
                toEnv: promoteConfirm.toEnv,
              });
              setPromoteConfirm(null);
              setPromoteAttested(false);
            }}
          >
            Stage prod promote
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};
